<template>
	<AlertBanner
		class="renewal-banner"
		:tone="banner.tier"
		:title="banner.title"
		:action-label="actionable ? banner.buttonLabel : undefined"
		@ds:action="emit('action')"
	>
		<template #icon>
			<!-- A static glyph from src/assets, inlined so the DS tile tints it (currentColor). -->
			<span
				class="renewal-banner__glyph"
				:class="`renewal-banner__glyph--${glyph}`"
				v-html="glyph === 'doc' ? DOC_SVG : WARNING_SVG"
			/>
		</template>
		<span class="renewal-banner__body">{{ banner.body }}</span>
	</AlertBanner>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { AlertBanner } from '@simple-life-app/growth-ui-kova'
import type { RenewalBannerModel } from '@/orders/renewalAction'
import { DOC_SVG, WARNING_SVG } from './orderGlyphs'

/**
 * The renewal card on the Orders list (Figma "Warning cards/ Desktop" 13868:139683 / 13718:100298 /
 * 13868:139507, intake 13966:164128 / 164036 / 164082, mobile 14254:170086): the DS `AlertBanner` at the
 * model's tier, the document glyph at Info and the warning triangle at Warning / Error, the model's title and
 * body. It draws what `renewalBannerModel` decided and never hides itself — only a read that drops the action
 * does (docs/architecture/orders.md B9).
 *
 * `actionable`: the card's button exists only when its destination does (`renewalCardDestination` is not
 * `null`); without one the card is text only — no disabled button, no "coming soon" (the intake, KOVA-801).
 * The host opens the destination on `action`.
 */

const props = defineProps<{
	banner: RenewalBannerModel
	actionable: boolean
}>()

const emit = defineEmits<{ action: [] }>()

/** Figma: the document at Info, the warning triangle at Warning and Error. */
const glyph = computed(() => (props.banner.tier === 'info' ? 'doc' : 'warning'))
</script>

<style scoped>
/* Figma's "Card subtitle" is 700 wide on desktop (13833:135955): an intake card's body wraps to two lines
   there rather than running up to the card's edge. A phone's column is narrower anyway. */
.renewal-banner__body {
	display: block;
	max-width: 700px;
}

.renewal-banner__glyph {
	display: flex;
}
</style>
